/**
 * Azure Blob Storage Service
 * Handles uploading files to Azure Blob Storage
 */

import { BLOB_STORAGE_CONFIG, getBlobStorageUrl as getConfigBlobUrl, getConfiguredSasToken } from '../config/blobStorageConfig';

/**
 * Uploads XML content to Azure Blob Storage using SAS token
 * @param {string} xmlContent - XML content to upload
 * @param {string} filename - Filename for the blob
 * @param {string} domainId - Domain identifier for the container path
 * @param {string} sasToken - SAS token for authentication (optional)
 * @returns {Promise<Object>} - Upload result with success status and URL
 */
export const uploadXmlToBlobStorage = async (xmlContent, filename, domainId, sasToken = null) => {
  try {
    // Construct the full URL for the blob
    let blobUrl = getConfigBlobUrl(domainId, filename);
    
    // Add SAS token if provided
    if (sasToken) {
      blobUrl += `?${sasToken}`;
    }
    
    const headers = {
      'Content-Type': 'application/xml',
      'x-ms-blob-type': 'BlockBlob',
      'x-ms-version': BLOB_STORAGE_CONFIG.apiVersion
    };

    const response = await fetch(blobUrl, {
      method: 'PUT',
      headers: headers,
      body: xmlContent
    });

    if (response.ok) {
      return {
        success: true,
        url: blobUrl,
        filename: filename,
        domainId: domainId
      };
    } else {
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }
  } catch (error) {
    return {
      success: false,
      error: error.message,
      filename: filename,
      domainId: domainId
    };
  }
};

/**
 * Gets a SAS token for blob storage access
 * Uses the configured SAS token or tries to get one from the backend
 * @returns {Promise<string|null>} - SAS token or null if not available
 */
export const getSasToken = async () => {
  try {
    return await getConfiguredSasToken();
  } catch (error) {
    return null;
  }
};

/**
 * Simulates XML upload for testing purposes when authentication is not available
 * @param {string} xmlContent - XML content to upload
 * @param {string} filename - Filename for the blob
 * @param {string} domainId - Domain identifier for the container path
 * @returns {Promise<Object>} - Simulated upload result
 */
export const simulateXmlUpload = async (xmlContent, filename, domainId) => {
  try {
    // Check if simulated uploads are enabled
    if (!BLOB_STORAGE_CONFIG.enableSimulatedUploads) {
      throw new Error('Simulated uploads are disabled');
    }
    
    // Simulate upload delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const blobUrl = getConfigBlobUrl(domainId, filename);
    
    return {
      success: true,
      url: blobUrl,
      filename: filename,
      domainId: domainId,
      simulated: true
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
      filename: filename,
      domainId: domainId
    };
  }
};

/**
 * Alternative method using Azure Storage SDK (if available)
 * This would require installing @azure/storage-blob package
 */
export const uploadXmlToBlobStorageWithSDK = async (xmlContent, filename, domainId) => {
  try {
    // This would require the Azure Storage SDK
    // const { BlobServiceClient } = require('@azure/storage-blob');
    
    // For now, return a mock response
    return {
      success: false,
      error: 'Azure SDK not configured',
      filename: filename,
      domainId: domainId
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
      filename: filename,
      domainId: domainId
    };
  }
};

/**
 * Validates if the domain ID is valid for blob storage
 * @param {string} domainId - Domain identifier
 * @returns {boolean} - Whether the domain ID is valid
 */
export const validateDomainId = (domainId) => {
  if (!domainId || typeof domainId !== 'string') {
    return false;
  }
  
  // Check for valid characters (alphanumeric, hyphens, underscores)
  const validDomainPattern = /^[a-zA-Z0-9-_]+$/;
  return validDomainPattern.test(domainId);
};

/**
 * Gets the blob storage URL for a given domain and filename
 * @param {string} domainId - Domain identifier
 * @param {string} filename - Filename
 * @returns {string} - Full blob storage URL
 */
export const getBlobStorageUrl = (domainId, filename) => {
  return getConfigBlobUrl(domainId, filename);
};
